/**
 * Fixgo Theme — theme.js
 * Scripts globaux, sans dépendance, chargés en différé (defer).
 *  1. Utilitaires (formatage des prix, annonces ARIA)
 *  2. Tiroirs (menu mobile, mini-panier, filtres) : focus piégé, Échap, verrouillage du scroll
 *  3. En-tête : état au défilement, sous-menus, panneau de recherche
 *  4. Panier AJAX : ajout, modification, rendu via la Section Rendering API
 *  5. Composants : carrousels, sélecteurs de quantité, auto-envoi de formulaires,
 *     bandeau d'annonce, vidéo du hero, recommandations, apparition au défilement
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* =========================================================
     1. Utilitaires
     ========================================================= */

  /** Formate un montant en centimes selon le format monétaire de la boutique. */
  theme.formatMoney = (cents, format = theme.moneyFormat || '{{amount}} €') => {
    const value = Number(cents || 0) / 100;
    const clean = String(format).replace(/<[^>]*>/g, '');
    const group = (n, decimals, thousands, decimal) => {
      const [int, frac] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return clean.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return group(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        default: return group(value, 2, ',', '.');
      }
    });
  };

  /** Annonce un message aux lecteurs d'écran. */
  theme.announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* =========================================================
     2. Tiroirs
     ========================================================= */
  let activeDrawer = null;
  let lastFocused = null;

  const isDesktopFilters = (drawer) =>
    drawer.classList.contains('drawer--filters') && window.matchMedia('(min-width: 990px)').matches;

  theme.openDrawer = (drawer, opener) => {
    if (!drawer || isDesktopFilters(drawer)) return;
    if (activeDrawer && activeDrawer !== drawer) theme.closeDrawer(false);
    lastFocused = opener || document.activeElement;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('scroll-lock');
    $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    activeDrawer = drawer;
    const panel = $('.drawer__panel', drawer);
    window.setTimeout(() => {
      const target = $('[data-drawer-close].icon-btn', panel) || $(FOCUSABLE, panel) || panel;
      target.focus();
    }, 60);
  };

  theme.closeDrawer = (restoreFocus = true) => {
    if (!activeDrawer) return;
    const drawer = activeDrawer;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-lock');
    $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    activeDrawer = null;
    if (restoreFocus && lastFocused && document.contains(lastFocused)) lastFocused.focus();
  };

  // Piège à focus : Tab reste dans le tiroir ouvert
  const trapFocus = (event) => {
    if (!activeDrawer || event.key !== 'Tab') return;
    const items = $$(FOCUSABLE, $('.drawer__panel', activeDrawer)).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  /* =========================================================
     3. En-tête
     ========================================================= */
  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;

    // Hauteur de l'en-tête exposée en CSS (barre collante, ancres)
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 100));

    // Ombre au défilement
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Sous-menus (<details>) : un seul ouvert, ouverture au survol sur desktop
    const dropdowns = $$('[data-dropdown]', header);
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    dropdowns.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) dropdowns.forEach((o) => { if (o !== d) o.open = false; });
      });
      if (canHover) {
        d.addEventListener('mouseenter', () => { d.open = true; });
        d.addEventListener('mouseleave', () => { d.open = false; });
      }
    });
    document.addEventListener('click', (e) => {
      dropdowns.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; });
    });
  };

  const toggleSearch = (force) => {
    const panel = document.getElementById('SearchPanel');
    if (!panel) return;
    const open = typeof force === 'boolean' ? force : panel.hidden;
    panel.hidden = !open;
    $$('[data-search-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) $('input[type="search"]', panel).focus();
  };

  /* =========================================================
     4. Panier AJAX
     ========================================================= */
  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
    $$('[data-cart-label]').forEach((el) => { el.textContent = (theme.strings.cartLabel || '').replace('__COUNT__', count); });
  };

  /** Remplace le contenu du mini-panier par le HTML rendu par Shopify. */
  const renderCartDrawer = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById('CartDrawerInner');
    const current = document.getElementById('CartDrawerInner');
    if (fresh && current) current.replaceWith(fresh);
    if (fresh) updateCartCount(Number(fresh.dataset.count || 0));
  };

  const useDrawer = () =>
    theme.cartType === 'drawer' && document.getElementById('CartDrawer') && !document.body.classList.contains('template-cart');

  const addToCart = async (form) => {
    const submit = form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]');
    const errorBox = form.querySelector('[data-form-error]');
    const body = new FormData(form);
    if (useDrawer()) {
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
    }

    form.classList.add('is-loading');
    if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }
    if (errorBox) errorBox.hidden = true;

    try {
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);

      if (useDrawer()) {
        renderCartDrawer(data.sections && data.sections['cart-drawer']);
        theme.openDrawer(document.getElementById('CartDrawer'), submit);
        theme.announce(theme.strings.added);
      } else {
        window.location.href = theme.routes.cart;
      }
    } catch (err) {
      if (errorBox) { errorBox.textContent = err.message || theme.strings.error; errorBox.hidden = false; }
      theme.announce(err.message || theme.strings.error);
    } finally {
      form.classList.remove('is-loading');
      if (submit) {
        submit.removeAttribute('aria-busy');
        // Le bouton reste désactivé si la variante est épuisée (géré par product.js)
        submit.disabled = submit.dataset.unavailable === 'true';
      }
    }
  };

  const changeCartLine = async (key, quantity) => {
    const inner = document.getElementById('CartDrawerInner');
    if (inner) inner.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || theme.strings.error);
      renderCartDrawer(data.sections && data.sections['cart-drawer']);
      const close = $('#CartDrawer [data-drawer-close].icon-btn');
      if (close) close.focus();
    } catch (err) {
      theme.announce(err.message || theme.strings.error);
      if (inner) inner.classList.remove('is-loading');
    }
  };

  /* =========================================================
     5. Composants
     ========================================================= */

  /** Carrousels : défilement natif + boutons précédent/suivant. */
  theme.initCarousels = (root = document) => {
    $$('[data-carousel]', root).forEach((carousel) => {
      if (carousel.dataset.ready) return;
      carousel.dataset.ready = 'true';
      const track = $('[data-carousel-track]', carousel);
      const prev = $('[data-carousel-prev]', carousel);
      const next = $('[data-carousel-next]', carousel);
      if (!track) return;

      const step = () => {
        const slide = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        carousel.classList.toggle('carousel--static', max <= 0);
      };
      const behavior = reducedMotion ? 'auto' : 'smooth';
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  };

  /** Bandeau d'annonce : rotation des messages. */
  const initAnnouncements = (root = document) => {
    $$('[data-announcement]', root).forEach((bar) => {
      const items = $$('[data-announcement-item]', bar);
      if (items.length < 2 || reducedMotion || bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      let index = 0;
      let paused = false;
      ['mouseenter', 'focusin'].forEach((ev) => bar.addEventListener(ev, () => { paused = true; }));
      ['mouseleave', 'focusout'].forEach((ev) => bar.addEventListener(ev, () => { paused = false; }));
      window.setInterval(() => {
        if (paused || document.hidden) return;
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, Number(bar.dataset.speed) || 5000);
    });
  };

  /** Vidéo du hero : bouton pause + respect de prefers-reduced-motion. */
  const initHero = (root = document) => {
    $$('[data-hero]', root).forEach((hero) => {
      const video = $('video', hero);
      const btn = $('[data-hero-pause]', hero);
      if (!video) return;
      video.muted = true;
      const setState = (playing) => {
        if (!btn) return;
        btn.setAttribute('aria-pressed', String(!playing));
        $('[data-icon-pause]', btn).hidden = !playing;
        $('[data-icon-play]', btn).hidden = playing;
      };
      if (reducedMotion) { video.pause(); video.removeAttribute('autoplay'); setState(false); }
      if (btn) {
        btn.addEventListener('click', () => {
          if (video.paused) { video.play(); setState(true); } else { video.pause(); setState(false); }
        });
      }
    });
  };

  /** Recommandations produit : chargées après le rendu de la page. */
  const initRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.loaded || !el.dataset.url) return;
      el.dataset.loaded = 'true';
      try {
        const res = await fetch(el.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          theme.initCarousels(el);
        }
      } catch (e) { /* silencieux : section facultative */ }
    });
  };

  /** Apparition douce des sections au défilement. */
  const initReveal = (root = document) => {
    if (!document.body.classList.contains('has-reveal') || reducedMotion || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    $$('[data-reveal]:not(.is-visible)', root).forEach((el) => { el.classList.add('reveal'); io.observe(el); });
  };

  /** Slider d'accueil : fondu automatique, points, pause (et respect de prefers-reduced-motion). */
  const initSliders = (root = document) => {
    $$('[data-slider]', root).forEach((slider) => {
      if (slider.dataset.ready) return;
      slider.dataset.ready = 'true';
      const slides = $$('[data-slide]', slider);
      const dots = $$('[data-slide-to]', slider);
      const pauseBtn = $('[data-slider-pause]', slider);
      if (slides.length < 2) return;
      let index = 0;
      let paused = reducedMotion;
      const show = (i) => {
        index = (i + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
        dots.forEach((d, n) => { d.classList.toggle('is-active', n === index); d.setAttribute('aria-current', n === index ? 'true' : 'false'); });
      };
      dots.forEach((d) => d.addEventListener('click', () => show(Number(d.dataset.slideTo))));
      const setPaused = (p) => {
        paused = p;
        if (!pauseBtn) return;
        pauseBtn.setAttribute('aria-pressed', String(p));
        $('[data-icon-pause]', pauseBtn).hidden = p;
        $('[data-icon-play]', pauseBtn).hidden = !p;
      };
      if (pauseBtn) pauseBtn.addEventListener('click', () => setPaused(!paused));
      setPaused(paused);
      let hover = false;
      slider.addEventListener('mouseenter', () => { hover = true; });
      slider.addEventListener('mouseleave', () => { hover = false; });
      slider.addEventListener('focusin', () => { hover = true; });
      slider.addEventListener('focusout', () => { hover = false; });
      window.setInterval(() => { if (!paused && !hover && !document.hidden) show(index + 1); }, Number(slider.dataset.interval) || 5000);
    });
  };

  /** Onglets accessibles (flèches gauche/droite, Début/Fin). */
  const initTabs = (root = document) => {
    $$('[data-tabs]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = 'true';
      const tabs = $$('[role="tab"]', wrap);
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          let n = null;
          if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') n = tabs[0];
          if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
      // Liens « Ajouter un avis » → ouvre l'onglet ciblé
      $$('[data-tab-link]').forEach((a) => a.addEventListener('click', () => {
        const t = tabs.find((x) => x.dataset.tab === a.dataset.tabLink);
        if (t) select(t);
      }));
    });
  };

  /** Barre de navigation fixée en haut au défilement (comme les boutiques « store »). */
  const initNavbar = () => {
    const bar = $('[data-navbar]');
    const spacer = $('[data-navbar-spacer]');
    if (!bar || !spacer) return;
    const update = () => {
      const top = spacer.getBoundingClientRect().top;
      const fixed = top < 0 && window.matchMedia('(min-width: 990px)').matches;
      bar.classList.toggle('is-fixed', fixed);
      spacer.style.height = fixed ? `${bar.offsetHeight}px` : '0px';
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', debounce(update, 100));
    update();
  };

  /** Recherche : si une catégorie est choisie sans mot-clé, on ouvre directement la collection. */
  const initSearchPill = () => {
    const form = $('[data-searchpill]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      const cat = $('[data-search-category]', form);
      const q = $('input[name="q"]', form);
      if (cat && cat.value && !q.value.trim()) { e.preventDefault(); window.location.href = cat.value; }
    });
  };

  /* =========================================================
     Écouteurs délégués
     ========================================================= */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      theme.openDrawer(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    if (e.target.closest('[data-drawer-close]')) { theme.closeDrawer(); return; }

    const cartLink = e.target.closest('[data-cart-toggle]');
    if (cartLink && useDrawer()) {
      e.preventDefault();
      theme.openDrawer(document.getElementById('CartDrawer'), cartLink);
      return;
    }

    if (e.target.closest('[data-search-toggle]')) { toggleSearch(); return; }

    const change = e.target.closest('[data-cart-change]');
    if (change) { changeCartLine(change.dataset.key, Number(change.dataset.qty)); return; }

    // Sélecteurs de quantité génériques (+ / -)
    const qtyBtn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (qtyBtn) {
      const input = $('input', qtyBtn.closest('[data-qty]'));
      if (!input) return;
      const min = Number(input.min || 0);
      const next = Number(input.value || 0) + (qtyBtn.hasAttribute('data-qty-plus') ? 1 : -1);
      input.value = Math.max(min, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (activeDrawer) { theme.closeDrawer(); return; }
      const panel = document.getElementById('SearchPanel');
      if (panel && !panel.hidden) { toggleSearch(false); $('[data-search-toggle]').focus(); return; }
      const openDropdown = $('[data-dropdown][open]');
      if (openDropdown) { openDropdown.open = false; $('summary', openDropdown).focus(); }
    }
    trapFocus(e);
  });

  // Ajout au panier sans rechargement (toutes les formes [data-product-form])
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('[data-product-form]') || !window.fetch) return;
    e.preventDefault();
    addToCart(form);
  });

  // Envoi automatique des formulaires [data-autosubmit] (filtres, tri, panier)
  const autoSubmit = debounce((form) => (form.requestSubmit ? form.requestSubmit() : form.submit()), 400);
  document.addEventListener('change', (e) => {
    const form = e.target.form;
    if (form && form.hasAttribute('data-autosubmit')) autoSubmit(form);
  });

  // Ferme le tiroir des filtres si on repasse en desktop
  window.addEventListener('resize', debounce(() => {
    if (activeDrawer && isDesktopFilters(activeDrawer)) theme.closeDrawer(false);
  }, 200));

  /* =========================================================
     Initialisation (+ rechargements dans l'éditeur de thème)
     ========================================================= */
  const init = (root = document) => {
    theme.initCarousels(root);
    initAnnouncements(root);
    initHero(root);
    initRecommendations(root);
    initReveal(root);
    initSliders(root);
    initTabs(root);
  };

  initHeader();
  initNavbar();
  initSearchPill();
  init();

  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-visible'));
  });
})();
